import RecordSubmitter from './-RecordSubmitter';
import Guidelines from './-Guidelines';
import SubmissionsTable from '~/routes/submissions/-SubmissionsTable';
import { ScrollArea } from '~/primitives/scroll-area';
import { ListTabSwitch } from '~/partials/TabSwitch';
import { Button } from '~/primitives/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import {
    submissionQueueQuery,
    mySubmissionsQuery,
    submissionStatusQuery,
} from '~/util/queries/public/submissions';
import { ExternalLink } from 'lucide-react';
import {
    createFileRoute,
    Link,
    stripSearchParams,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useAuthData } from '~/components/Providers/AuthProvider';
import { useList } from '~/util/hooks/useList';
import { fetchPublicAsset, seo } from '~/util';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { synchronizeWithPemonlist } from '~/util/mutations/public/submissions';
import {
    defaultUserSubmissionsParams,
    userSubmissionsParamsSchema,
} from './-SearchParams';
import { guidelinesPath } from '../guidelines/route';

export const Route = createFileRoute('/submissions')({
    validateSearch: userSubmissionsParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultUserSubmissionsParams)],
    },
    loaderDeps: ({
        search: { list, level, mobile, status, page, per_page },
    }) => ({ list, level, mobile, status, page, per_page }),
    loader: async ({
        context: { queryClient, authed },
        deps: { list, ...filters },
    }) => {
        const [data, queue, submissionsEnabled, guidelines] = await Promise.all(
            [
                authed
                    ? queryClient.fetchQuery(
                          mySubmissionsQuery({ list, filters }),
                      )
                    : null,
                queryClient.fetchQuery(submissionQueueQuery({ list })),
                queryClient.fetchQuery(submissionStatusQuery({ list })),
                fetchPublicAsset(`${guidelinesPath(list)}/Submission_Page.md`)
                    .then((res) => {
                        if (!res.ok) {
                            console.error(
                                'Failed to fetch guidelines summary:',
                                res,
                            );
                            return 'Failed to load guidelines summary.';
                        }
                        return res.text();
                    })
                    .catch((e) => {
                        console.error('Failed to fetch guidelines summary:', e);
                        return 'Failed to fetch guidelines summary.';
                    }),
            ],
        );

        return {
            data,
            queue,
            submissionsEnabled,
            guidelines,
        };
    },
    head: () => ({
        meta: seo({
            title: 'Record Submissions',
        }),
    }),
    component: Submissions,
});

export const DisabledSubmissionsMessage = () => {
    const list = useList();
    return (
        <div className="m-7 p-4 bg-red-900/30 rounded-lg text-center">
            <span className="text-lg font-semibold text-red-200">
                {list.charAt(0).toUpperCase() + list.slice(1)} submissions are
                currently closed, please try again later.
            </span>
        </div>
    );
};

function Submissions() {
    const { username } = useAuthData() ?? {};

    const [leftPanelViewport, setLeftPanelViewport] =
        useState<HTMLDivElement | null>(null);

    const list = useList();
    const { queue, submissionsEnabled } = Route.useLoaderData({
        select: ({ queue, submissionsEnabled }) => ({
            queue,
            submissionsEnabled,
        }),
    });

    const mutationPemonlist = useApiMutation(synchronizeWithPemonlist());

    return (
        <div className="flex flex-col lg:flex-row gap-3">
            <div className="w-full lg:w-7/12 lg:h-[calc(100vh-150px)] bg-background-dark rounded-xl py-2">
                <ScrollArea
                    className="h-full"
                    viewportRef={setLeftPanelViewport}
                >
                    <div className="h-full items-center flex flex-col mx-3 clamp-[pt-4-14-clamp]">
                        <h1 className="text-3xl font-bold text-secondary-foreground">
                            RECORD SUBMISSION
                        </h1>

                        <p className="text-center text-secondary-light py-2">
                            There are currently{' '}
                            {queue?.regular_submissions_in_queue +
                                queue?.priority_submissions_in_queue}{' '}
                            submissions in the queue.
                        </p>

                        {username ? (
                            <>
                                <ListTabSwitch
                                    from="/submissions"
                                    className="my-2 w-5/6"
                                    size="lg"
                                />
                                {list === 'platformer' && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="h-12 my-2"
                                                onClick={() =>
                                                    mutationPemonlist.mutateAsync()
                                                }
                                            >
                                                <img
                                                    src="/assets/partners/PEMONLIST_2.webp"
                                                    alt="Pemonlist Icon"
                                                    className="w-10 h-10 mr-2"
                                                />
                                                Synchronize with Pemonlist
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="right"
                                            className="bg-black text-white ml-2"
                                        >
                                            <p className="text-xs font-semibold w-lg">
                                                Here, you can import/update all
                                                of your platformer records from
                                                Pemonlist, to your AREDL
                                                Profile. For this to work, your
                                                pemonlist account needs to be
                                                linked to the same discord
                                                account you&apos;re logged in
                                                with on AREDL.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}

                                {submissionsEnabled !== false ? (
                                    <RecordSubmitter />
                                ) : (
                                    <DisabledSubmissionsMessage />
                                )}
                                <h2>Previous Submissions</h2>
                                <SubmissionsTable
                                    scrollElement={leftPanelViewport}
                                />
                            </>
                        ) : (
                            <p className="mt-10 text-xl text-secondary-foreground">
                                You need to be logged in to submit a record.
                            </p>
                        )}
                    </div>
                </ScrollArea>
            </div>
            <div className="w-full lg:w-5/12 lg:h-[calc(100vh-150px)] bg-background-dark rounded-xl flex flex-col py-9 px-8">
                <ScrollArea className="pr-6">
                    <div className="flex justify-center items-center gap-x-2 w-fit clamp-[text-xl-3xl-clamp] border-b border-secondary-foreground pb-3 group cursor-pointer">
                        <h1 className="clamp-[text-xl-3xl-clamp] leading-none">
                            <Link
                                to="/guidelines"
                                search={({ list }) => ({ list })}
                                target="_blank"
                                className="text-white group-hover:text-primary transition-colors"
                            >
                                Submission Guidelines
                            </Link>
                        </h1>
                        <ExternalLink
                            height={30}
                            width={30}
                            className="text-white group-hover:text-primary transition-colors"
                            onClick={() =>
                                window.open('/guidelines/', '_blank')
                            }
                        />
                    </div>

                    <Guidelines />
                </ScrollArea>
            </div>
        </div>
    );
}
